/**
 * writebackService — web read/write layer for the f8 write-back loop (PPL-265).
 *
 * Port of the app's `services/writebackService.js`, sharing its contract
 * knowledge verbatim; if a rule below surprises you, the app file carries the
 * same rule with the same reasoning.
 *
 * Reads are DIRECT Firestore subscriptions: `users/{uid}/suggestions/**` is
 * owner-readable but Admin-only for writes (WS-0a-1 carve-out). Every
 * mutation goes through a callable — a client write on these paths is denied
 * by rules on purpose, because a client that could write them could forge
 * provenance.
 */

import {
  collection,
  getCountFromServer,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type Timestamp,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  FIRESTORE_DB,
  editTimelineEntryEurW,
  logHangoutEurW,
  respondToSuggestionEurW,
  undoWritebackItemEurW,
} from '../firebaseConfig';

/** Cap the live query — the review queue is meant to reach zero, not scroll. */
export const PENDING_PAGE_SIZE = 100;

/** Cap the receipts query — a receipt is a recent event, not an archive. */
export const AUTO_SAVED_PAGE_SIZE = 25;

/** How long an auto-saved item keeps offering its undo. */
export const AUTO_SAVED_WINDOW_MS = 7 * 86400000;

export type ConfidenceBand = 'high' | 'medium' | 'low';

/**
 * Display-only band edges (PPL-454). Mirrors the server's analytics band
 * edges so the words a user reads and the bands a dashboard groups by are
 * the same three zones — but this is NEVER a gate. Whether an item saved
 * itself or waits for review is the server's decision and already encoded in
 * the doc; re-deriving it here is how a client starts describing a policy it
 * no longer implements.
 */
export const CONFIDENCE_BAND_EDGES = { high: 0.85, medium: 0.5 };

export function confidenceBand(c: number | null): ConfidenceBand | null {
  if (c === null || !Number.isFinite(c)) return null;
  if (c >= CONFIDENCE_BAND_EDGES.high) return 'high';
  if (c >= CONFIDENCE_BAND_EDGES.medium) return 'medium';
  return 'low';
}

/**
 * How an item's match reads: "john · 92%", "likely john · 72%", "maybe john
 * · 40%". Degrades to the bare name when no score travelled with the doc (an
 * older suggestion, a fixture), which is exactly what surfaces rendered
 * before this existed. No string here names the threshold — a per-item score
 * is data, the config number is not copy.
 */
export function matchLabel(
  name: string | null,
  band: ConfidenceBand | null,
  pct: number | null,
): string | null {
  if (!name) return null;
  if (pct === null) return name;
  const prefix = band === 'medium' ? 'likely ' : band === 'low' ? 'maybe ' : '';
  return `${prefix}${name} · ${pct}%`;
}

/**
 * "could be john or jon" — the headline for an item f8 could not pin to one
 * person. No percentage: the question is which, not how sure.
 */
export function candidateSummary(names: Array<string | null | undefined>): string | null {
  const clean = names.map((n) => (n || '').trim()).filter(Boolean);
  if (clean.length < 2) return null;
  if (clean.length === 2) return `could be ${clean[0]} or ${clean[1]}`;
  const others = clean.length - 2;
  return `could be ${clean[0]}, ${clean[1]} or ${others} other${others === 1 ? '' : 's'}`;
}

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export type SuggestionKind = 'fact' | 'event' | 'date' | 'commitment' | 'note' | 'identity';

export interface SuggestionCandidate {
  personId: string;
  displayName: string;
  confidence: number;
}

/** A suggestion normalized for rendering — the only shape pages consume. */
export interface SuggestionItem {
  id: string;
  type: string;
  kind: SuggestionKind;
  personId: string | null;
  personName: string | null;
  candidates: SuggestionCandidate[];
  /** Extracted content, never authored copy. */
  text: string;
  /** Short label for the value chip's caption ("work", "birthday", …). */
  field: string;
  /** Previous value when the suggestion is a change to a known field. */
  from: string | null;
  /** The value chip. */
  to: string | null;
  /** Payload key an inline edit writes, when editing is safe at all. */
  editKey: string | null;
  editValue: string | null;
  /**
   * Inline editing is offered ONLY when the chip value IS the payload field
   * the edit writes. Anywhere they differ, an edit box would look like it
   * corrects the sentence while actually overwriting a different field —
   * e.g. a `date`'s sentence vs its `label`, or `new_person` where the text
   * would land in `firstName` AND teach the alias resolver.
   */
  editable: boolean;
  /** "from your chat with f8, tuesday" */
  src: string;
  mentionText: string | null;
  contextHint: string | null;
  /**
   * The resolver's confidence in WHO this item is about (PPL-454), 0..1, as
   * the server already sends it on `provenance.confidence`. `null` when the
   * doc carries none, or when showing it would describe the wrong
   * proposition — see describeSuggestion.
   */
  confidence: number | null;
  confidencePct: number | null;
  confidenceBand: ConfidenceBand | null;
  createdAt: Date | null;
  expiresAt: Date | null;
  raw: Record<string, unknown>;
}

export interface RespondResult {
  success: true;
  status: 'accepted' | 'edited' | 'rejected' | 'conflicted' | 'already_resolved';
  suggestionId: string;
  personId: string | null;
  personName: string | null;
  entryId: string | null;
  createdPerson: boolean;
  previousStatus?: string;
}

export interface UndoResult {
  success: true;
  status: 'undone' | 'already_undone' | 'gone';
  revertedPaths: string[];
  conflictedPaths: string[];
}

export interface SavedLedger {
  total: number;
  people: number;
  byKind: Partial<Record<SuggestionKind, number>>;
}

/* ------------------------------------------------------------------ *
 * Presentation mapping — mirrors the app service exactly
 * ------------------------------------------------------------------ */

const KIND_BY_TYPE: Record<string, SuggestionKind> = {
  fact: 'fact',
  cadence: 'fact',
  date: 'date',
  commitment: 'commitment',
  log_interaction: 'event',
  calendar_attendee: 'event',
  relationship_signal: 'note',
  import_summary: 'note',
  link_mention: 'identity',
  new_person: 'identity',
};

/**
 * Types the SERVER treats as identity (`callables.ts` IDENTITY_TYPES).
 *
 * Two consequences on this side. The user is being asked WHO, not WHAT, so
 * they get a chooser; and a plain `undo` on one of these ALREADY tombstones
 * the alias, so offering "not <name>?" beside it is a second verb that does
 * exactly what the first one does — which is worse than not offering it,
 * because the two read as different answers.
 */
export const IDENTITY_TYPES: readonly string[] = [
  'link_mention',
  'new_person',
  'calendar_attendee',
];

const CHANNEL_LABEL: Record<string, string> = {
  f8_text: 'your chat with f8',
  f8_voice: 'your voice chat',
  telegram: 'telegram',
  whatsapp: 'whatsapp',
  whatsapp_import: 'an imported whatsapp chat',
  email_inbound: 'a forwarded email',
  app_intent: 'a quick capture',
  quick_capture: 'a quick capture',
  device_calendar: 'your calendar',
  google_calendar: 'your google calendar',
};

/** Payload keys `mergeEdits` accepts server-side; anything else is dropped. */
const EDITABLE_KEYS: Record<string, readonly string[]> = {
  link_mention: ['mentionText'],
  new_person: ['mentionText', 'firstName', 'lastName', 'nickName'],
  log_interaction: ['kind', 'whenIso', 'summary'],
  fact: ['fact', 'field', 'value'],
  date: ['kind', 'dateIso', 'label', 'dobFormat'],
  commitment: ['text', 'dueIso'],
  relationship_signal: ['note', 'polarity'],
  // The address is evidence, not prose — the server refuses mentionText edits
  // for this type; the name fields feed accept-as-new (PPL-275).
  calendar_attendee: ['firstName', 'lastName', 'nickName'],
  cadence: ['contactFrequency'],
  import_summary: [],
};

function editableTextKey(type: string): string | null {
  const keys = EDITABLE_KEYS[type] || [];
  for (const candidate of ['fact', 'text', 'note', 'summary', 'label', 'mentionText']) {
    if (keys.includes(candidate)) return candidate;
  }
  return null;
}

function toDate(ts: unknown): Date | null {
  if (!ts) return null;
  const t = ts as Timestamp;
  if (typeof t.toDate === 'function') return t.toDate();
  const d = new Date(ts as string);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "tuesday" for the last week, then "12 aug". */
function provenanceWhen(date: Date | null): string {
  if (!date) return '';
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return date.toLocaleDateString(undefined, { weekday: 'long' }).toLowerCase();
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }).toLowerCase();
}

/** `YYYY-MM-DD` or `--MM-DD` (no-year birthday) rendered for a human. */
function humanDate(dateIso: unknown): string {
  if (typeof dateIso !== 'string') return '';
  const noYear = dateIso.startsWith('--');
  const parts = (noYear ? `2000-${dateIso.slice(2)}` : dateIso).split('-');
  if (parts.length !== 3) return dateIso;
  const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
  if (Number.isNaN(d.getTime())) return dateIso;
  return d.toLocaleDateString(
    undefined,
    noYear ? { day: 'numeric', month: 'long' } : { day: 'numeric', month: 'long', year: 'numeric' },
  );
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/** Normalize one raw suggestion doc into what the page renders. */
export function describeSuggestion(
  raw: Record<string, any>,
  personName: string | null,
): SuggestionItem {
  const p = (raw.payload || {}) as Record<string, unknown>;
  const kind = KIND_BY_TYPE[raw.type] || 'note';
  const who = personName || str(p.mentionText) || 'them';

  let text = '';
  let field: string = kind;
  let from: string | null = null;
  let to: string | null = null;

  switch (raw.type) {
    case 'fact':
      text = str(p.fact);
      field = 'fact';
      to = text;
      break;
    case 'date': {
      const when = humanDate(p.dateIso);
      const label =
        p.kind === 'birthday' ? 'birthday'
          : p.kind === 'anniversary' ? 'anniversary'
            : (str(p.label) || 'date');
      text = `${who}'s ${label} is ${when}`;
      field = label;
      to = when;
      break;
    }
    case 'commitment':
      text = str(p.text);
      field = 'commitment';
      to = text;
      break;
    case 'log_interaction': {
      const summary = str(p.summary);
      const kindWord = str(p.kind) || 'caught up';
      text = summary || `${kindWord} with ${who}`;
      field = 'interaction';
      to = summary || kindWord;
      break;
    }
    case 'relationship_signal':
      text = str(p.note);
      field = 'relationship note';
      to = text;
      break;
    case 'new_person':
      text = `${str(p.mentionText) || who} isn't in your ppl yet`;
      field = 'new person';
      to = str(p.mentionText) || who;
      break;
    case 'link_mention':
      text = `you mentioned ${str(p.mentionText) || who}`;
      field = 'who this is';
      to = personName;
      break;
    case 'calendar_attendee': {
      // The payload's mentionText is an ADDRESS or a title-mined name —
      // attendee display names never leave the device (PPL-273).
      const mention = str(p.mentionText) || who;
      const eventTitle = str(p.eventTitle);
      text = eventTitle ? `${mention} is on "${eventTitle}" with you` : `${mention} is on your calendar`;
      field = 'from your calendar';
      to = personName || mention || null;
      break;
    }
    default:
      text = str(p.fact) || str(p.note) || str(p.text) || str(p.summary) || str(p.mentionText);
      to = text || null;
  }

  const capturedAt = toDate(raw.provenance?.capturedAt);
  const channel = CHANNEL_LABEL[raw.provenance?.channel] || 'your conversation';
  const when = provenanceWhen(capturedAt);

  const editKey = editableTextKey(raw.type);
  const editValueRaw = editKey ? p[editKey] : null;
  const editValue = typeof editValueRaw === 'string' ? editValueRaw : null;

  // PPL-454 — the match score. Already on every doc since PPL-265; the
  // clients simply threw it away. `new_person` is the one type that must
  // never show one: its confidence is the best NON-match score, so rendering
  // it reads as confidence in the opposite proposition.
  const rawConfidence = raw.provenance?.confidence;
  const confidence =
    raw.type !== 'new_person' &&
    typeof rawConfidence === 'number' &&
    Number.isFinite(rawConfidence) &&
    rawConfidence > 0
      ? rawConfidence
      : null;
  const confidencePct =
    confidence === null ? null : Math.max(0, Math.min(100, Math.round(confidence * 100)));

  return {
    id: raw.suggestionId || raw.id,
    type: raw.type,
    kind,
    personId: raw.personId || null,
    personName,
    candidates: Array.isArray(raw.candidates) ? raw.candidates : [],
    text,
    field,
    from,
    to,
    editKey,
    editValue,
    // The web card edits the VALUE CHIP, so the gate is chip === payload field.
    editable: !!editKey && !!editValue && editValue.trim() === (to || '').trim(),
    src: when ? `from ${channel}, ${when}` : `from ${channel}`,
    mentionText: str(p.mentionText) || null,
    contextHint: str(p.contextHint) || null,
    confidence,
    confidencePct,
    confidenceBand: confidenceBand(confidence),
    createdAt: toDate(raw.createdAt),
    expiresAt: toDate(raw.expiresAt),
    raw,
  };
}

/* ------------------------------------------------------------------ *
 * Reads
 * ------------------------------------------------------------------ */

/**
 * Live pending suggestions. Ordered createdAt ASCENDING to match the deployed
 * composite index exactly (`suggestions`: status ASC, createdAt ASC) — a
 * missing index on this path returns NOTHING, indistinguishable from an empty
 * queue. Display order is applied by the consumer.
 */
export function subscribePendingSuggestions(
  uid: string,
  onData: (rows: Record<string, any>[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'suggestions'),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc'),
    fsLimit(PENDING_PAGE_SIZE),
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows: Record<string, any>[] = [];
      // Data spread FIRST so the true Firestore doc id always wins — a doc
      // body carrying its own `id` field would otherwise clobber it, and this
      // id is what resolve/dismiss/undo send back to the callables.
      snap.forEach((d) => rows.push({ ...d.data(), id: d.id }));
      onData(rows);
    },
    (error) => {
      console.warn('[writeback] pending subscription failed:', error?.message);
      if (onError) onError(error);
    },
  );
}

/**
 * Live receipts for the things f8 saved BY ITSELF (PPL-454), newest first.
 *
 * A bare `orderBy('appliedAt')` with no `where`, on purpose. Firestore's
 * automatic single-field indexes are SPARSE — a document without the field
 * is not in the index at all — and `appliedAt` is written only for an
 * auto-applied item (a user accept writes `resolvedAt`, never this). So this
 * query returns exactly the auto-saved docs with no composite index and
 * nothing to add to firestore.indexes.json.
 *
 * Do NOT "tidy" this into `where('status','==','accepted')`: that pairing
 * DOES need a composite index, and a missing index returns NOTHING — which
 * looks identical to "f8 has saved nothing", the one failure the user cannot
 * tell from working software.
 *
 * Status and the 7-day window are therefore applied in the handler. Undo
 * DELETES `appliedAt` server-side, so an undone receipt leaves the sparse
 * index altogether and this fixed-size page stays exact; the status check is
 * the second lock, for a row undone before that was true.
 */
export function subscribeAutoSavedSuggestions(
  uid: string,
  onData: (rows: Record<string, any>[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'suggestions'),
    orderBy('appliedAt', 'desc'),
    fsLimit(AUTO_SAVED_PAGE_SIZE),
  );
  return onSnapshot(
    q,
    (snap) => {
      const cutoff = Date.now() - AUTO_SAVED_WINDOW_MS;
      const rows: Record<string, any>[] = [];
      // Data spread FIRST so the true Firestore doc id wins — same reason as
      // the pending query: this id is what undo sends back.
      snap.forEach((d) => {
        const data = d.data();
        if (data.status !== 'accepted' || data.autoApplied !== true) return;
        const at = toDate(data.appliedAt);
        if (!at || at.getTime() < cutoff) return;
        rows.push({ ...data, id: d.id });
      });
      onData(rows);
    },
    (error) => {
      console.warn('[writeback] auto-saved subscription failed:', error?.message);
      if (onError) onError(error);
    },
  );
}

/**
 * The sidebar ledger: what has already been saved, by kind.
 *
 * Derived from resolved suggestion docs rather than a timeline
 * collection-group query, because the timeline rules match is path-anchored
 * (`users/{uid}/people/{pid}/timeline`) — a collectionGroup('timeline') read
 * is denied by rules AND has no COLLECTION_GROUP index. Suggestion docs are
 * owner-readable, carry `type` + `personId`, and `in` on one field needs no
 * composite index.
 */
export async function fetchSavedLedger(uid: string): Promise<SavedLedger> {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'suggestions'),
    where('status', 'in', ['accepted', 'edited']),
    fsLimit(500),
  );
  const snap = await getDocs(q);
  const byKind: Partial<Record<SuggestionKind, number>> = {};
  const people = new Set<string>();
  snap.forEach((d) => {
    const data = d.data();
    const kind = KIND_BY_TYPE[data.type] || 'note';
    byKind[kind] = (byKind[kind] || 0) + 1;
    if (typeof data.personId === 'string' && data.personId) people.add(data.personId);
  });
  return { total: snap.size, people: people.size, byKind };
}

/** Lightweight count used by surfaces that only need the number. */
export async function countPendingSuggestions(uid: string): Promise<number> {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'suggestions'),
    where('status', '==', 'pending'),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/** Resolve a people subcollection snapshot into {id → display name}. */
export function subscribePeopleNames(
  uid: string,
  onData: (people: Record<string, { name: string; firstName: string | null }>) => void,
): Unsubscribe {
  return onSnapshot(
    collection(FIRESTORE_DB, 'users', uid, 'people'),
    (snap) => {
      const next: Record<string, { name: string; firstName: string | null }> = {};
      snap.forEach((d) => {
        const p = d.data();
        const name =
          p.nickname || p.nickName || p.firstName ||
          `${p.firstName || ''} ${p.lastName || ''}`.trim() || '';
        if (name) next[d.id] = { name, firstName: p.firstName || p.nickname || p.nickName || null };
      });
      onData(next);
    },
    (err) => console.warn('[writeback] people subscription failed:', err?.message),
  );
}

/* ------------------------------------------------------------------ *
 * Writes — callables only
 * ------------------------------------------------------------------ */

export async function respondToSuggestion(input: {
  suggestionId: string;
  action: 'accept' | 'edit' | 'reject';
  edits?: Record<string, string>;
  chosenPersonId?: string;
}): Promise<RespondResult | null> {
  const payload: Record<string, unknown> = {
    suggestionId: input.suggestionId,
    action: input.action,
  };
  if (input.edits && Object.keys(input.edits).length) payload.edits = input.edits;
  if (input.chosenPersonId) payload.chosenPersonId = input.chosenPersonId;
  const res = await respondToSuggestionEurW(payload);
  return (res?.data as RespondResult) || null;
}

/**
 * Undo an ACCEPTED item.
 *
 * Default (no reason) means "wrong fact": the writes are reverted and no
 * alias is tombstoned — today's behaviour, unchanged, for every caller that
 * does not opt in.
 *
 * `reason: 'wrong_person'` is the second affordance on an auto-saved receipt
 * ("not john?"): it reverts AND tombstones the alias, so f8 stops linking
 * that mention to that person. Additive by contract — an older server
 * ignores the field rather than refusing the call.
 */
export async function undoWritebackItem(
  suggestionId: string,
  reason?: 'wrong_person',
): Promise<UndoResult | null> {
  const res = await undoWritebackItemEurW({ suggestionId, ...(reason ? { reason } : {}) });
  return (res?.data as UndoResult) || null;
}

/**
 * Remove one timeline entry (the callable's {personId, entryId} addressing
 * mode). Terminal for the same reason as suggestion undo — the alias is
 * tombstoned so f8 will not re-suggest the fact. Callers confirm FIRST;
 * there is no un-remove.
 */
export async function removeTimelineEntry(personId: string, entryId: string): Promise<UndoResult | null> {
  const res = await undoWritebackItemEurW({ personId, entryId });
  return (res?.data as UndoResult) || null;
}

/**
 * Log a hangout — a user-authored `log_interaction` timeline entry
 * (PPL-328). No review step: there is nothing to vet in what the user typed
 * themselves. Server enforces a 30/day cap (`resource-exhausted`).
 */
export async function logHangout(input: {
  personId: string;
  kind?: string;
  summary?: string;
  feeling?: string;
  whenIso?: string;
}): Promise<{ success: true; entryId: string } | null> {
  const res = await logHangoutEurW(input);
  return (res?.data as { success: true; entryId: string }) || null;
}

/**
 * Content-only edit of an ACTIVE timeline entry (PPL-329). Provenance is
 * immutable server-side; `failed-precondition` means the entry settled
 * (undone/conflicted) between render and save.
 */
export async function editTimelineEntry(
  personId: string,
  entryId: string,
  edits: { title?: string; detail?: string },
): Promise<boolean> {
  const res = await editTimelineEntryEurW({ personId, entryId, edits });
  return (res?.data as { success?: boolean })?.success === true;
}

/** Backend type/kind string → visual kind, for timeline rendering. */
export function kindOf(type: string): SuggestionKind {
  return KIND_BY_TYPE[type] || 'note';
}

/** Channel id → the "from your …" phrase; the service owns this vocabulary. */
export function channelLabelOf(channel: string | undefined): string {
  return (channel && CHANNEL_LABEL[channel]) || 'your conversation';
}

export interface TimelineEntry {
  id: string;
  entryId: string;
  kind: string;
  title: string;
  detail: string;
  at: unknown;
  provenance?: { channel?: string; capturedAt?: unknown };
  status: string;
  /** Structured mood from a logged hangout — clients render the wording. */
  feeling?: string;
  /** Set by editTimelineEntryEurW; surfaces render an "edited" marker. */
  editedByUser?: boolean;
}

/**
 * Live ACTIVE timeline entries for one person, most recent first. Matches the
 * deployed composite index exactly (`timeline` COLLECTION: status ASC, at
 * DESC) — like the suggestions query, a missing index here returns nothing.
 */
export function subscribePersonTimeline(
  uid: string,
  personId: string,
  onData: (rows: TimelineEntry[]) => void,
  onError?: (e: Error) => void,
): Unsubscribe {
  const q = query(
    collection(FIRESTORE_DB, 'users', uid, 'people', personId, 'timeline'),
    where('status', '==', 'active'),
    orderBy('at', 'desc'),
    fsLimit(50),
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows: TimelineEntry[] = [];
      snap.forEach((d) => rows.push({ ...(d.data() as Omit<TimelineEntry, 'id'>), id: d.id }));
      onData(rows);
    },
    (error) => {
      console.warn('[writeback] timeline subscription failed:', error?.message);
      if (onError) onError(error);
    },
  );
}

/**
 * Fire-and-forget reject for the pagehide path ONLY.
 *
 * A normal `httpsCallable` awaited inside a pagehide handler is routinely
 * aborted at page teardown, so a deferred dismissal flushed that way could
 * silently never reach the server — the item would be back in the queue next
 * visit, the exact lie the deferred-dismiss design exists to avoid. `fetch`
 * with `keepalive: true` is the mechanism browsers provide for this moment
 * (sendBeacon can't carry the Authorization header a callable requires).
 *
 * Speaks the callable wire protocol directly: POST {data: …} with a Bearer ID
 * token. The token must be pre-fetched — there is no time for an async
 * getIdToken() during teardown — which is why the caller passes it in.
 */
export function rejectSuggestionKeepalive(suggestionId: string, idToken: string): void {
  const projectId = (FIRESTORE_DB.app.options as { projectId?: string }).projectId;
  if (!projectId) return;
  const url = `https://europe-west1-${projectId}.cloudfunctions.net/respondToSuggestionEurW`;
  try {
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ data: { suggestionId, action: 'reject' } }),
    });
  } catch {
    // Best-effort by definition — the page is going away.
  }
}
